/**
 * マイプレイス編集ダイアログ
 *
 * マイプレイスの名前・半径・WiFiパターンを編集する
 */

import { Loader2, MapPin, Plus, Wifi, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { updateMyPlace } from '@/api/services/overland'
import type { MyPlace, Place } from '@/api/types/overland'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitLock } from '@/hooks/useSubmitLock'

/** MyPlaceとPlaceの両方から呼び出せるようにする */
type EditablePlace = MyPlace | Place

type Props = {
  place: EditablePlace | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: (updated: MyPlace) => void
}

/** PlaceまたはMyPlaceから表示名を取得 */
function getPlaceName(place: EditablePlace): string {
  if ('display_name' in place) return place.display_name
  return place.name
}

export const MyPlaceEditDialog = ({ place, open, onOpenChange, onUpdated }: Props) => {
  const [name, setName] = useState('')
  const [radius, setRadius] = useState(100)
  const [wifiPatterns, setWifiPatterns] = useState<string[]>([])
  const [newWifiPattern, setNewWifiPattern] = useState('')
  const { isSubmitting: isSaving, run } = useSubmitLock()

  // placeが変わったらフォームをリセット
  useEffect(() => {
    if (!place || !open) return
    setName(getPlaceName(place))
    setRadius(place.radius)
    setWifiPatterns(place.wifi_patterns ?? [])
    setNewWifiPattern('')
  }, [place, open])

  const handleAddWifiPattern = useCallback(() => {
    const pattern = newWifiPattern.trim()
    if (!pattern) return
    if (wifiPatterns.includes(pattern)) {
      toast.error('同じパターンが既に存在します')
      return
    }
    setWifiPatterns((prev) => [...prev, pattern])
    setNewWifiPattern('')
  }, [newWifiPattern, wifiPatterns])

  const handleRemoveWifiPattern = useCallback((index: number) => {
    setWifiPatterns((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = useCallback(async () => {
    if (!place) return
    if (!name.trim()) {
      toast.error('名前を入力してください')
      return
    }

    await run(async () => {
      try {
        const updated = await updateMyPlace(place.id, {
          name: name.trim(),
          radius,
          wifi_patterns: wifiPatterns,
        })
        toast.success(`「${updated.name}」を更新しました`)
        onUpdated(updated)
        onOpenChange(false)
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }, [place, name, radius, wifiPatterns, onUpdated, onOpenChange, run])
  if (!place) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="size-4" />
            マイプレイスを編集
          </DialogTitle>
          <DialogDescription>名前や検出設定を変更できます</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* 名前 */}
          <div className="space-y-2">
            <Label htmlFor="place-name">名前</Label>
            <Input id="place-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="例: 自宅" />
          </div>

          {/* 半径 */}
          <div className="space-y-2">
            <Label htmlFor="place-radius">検出半径 (m)</Label>
            <Input
              id="place-radius"
              type="number"
              min={10}
              max={5000}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">この半径内に入ると訪問として検出されます</p>
          </div>

          {/* 緯度・経度（読み取り専用） */}
          <div className="space-y-2">
            <Label>座標</Label>
            <p className="text-sm text-muted-foreground">
              {place.latitude.toFixed(6)}, {place.longitude.toFixed(6)}
            </p>
          </div>

          {/* WiFiパターン */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Wifi className="size-3.5" />
              WiFiパターン
            </Label>
            <p className="text-xs text-muted-foreground">WiFi名の部分一致でこの場所を自動検出します</p>

            {/* 既存パターン一覧 */}
            {wifiPatterns.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {wifiPatterns.map((pattern, index) => (
                  <span
                    key={`${pattern}-${index}`}
                    className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs"
                  >
                    {pattern}
                    <button
                      type="button"
                      onClick={() => handleRemoveWifiPattern(index)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* 新規パターン追加 */}
            <div className="flex gap-2">
              <Input
                value={newWifiPattern}
                onChange={(e) => setNewWifiPattern(e.target.value)}
                placeholder="WiFi名パターン"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddWifiPattern()
                  }
                }}
              />
              <Button type="button" variant="outline" size="icon" onClick={handleAddWifiPattern} className="shrink-0">
                <Plus className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                保存中...
              </>
            ) : (
              '保存'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
